import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Gift, Minus, Plus, Tag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js';

import useGetEvent from 'features/common/hooks/useGetEvent';
import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import { useSeatsSelection } from 'features/common/hooks/useSeatsSelection';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { PunchupPageContent } from 'features/common/components/Punchup/primitives/layout/PunchupPageContent';
import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import { PunchupBuyerSeatsWidget } from 'features/common/components/Punchup/composites/seats/PunchupBuyerSeatsWidget';
import {
	PunchupFilterSelect,
	PunchupFilterOption,
} from 'features/common/components/Punchup/primitives/forms/pickers/PunchupFilterSelect';
import {
	PunchupCombobox,
	ComboboxOption,
} from 'features/common/components/Punchup/primitives/forms/pickers/PunchupCombobox';
import { TicketType } from 'features/common/types';
import { post, getData } from 'services/api';
import { postToast } from 'features/common/utils/postFlash';
import { camelize } from 'utils/utils';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store';

import { CardPaymentForm, CardPaymentFormHandle } from './CardPaymentForm';
import { NewPromotionForm } from './NewPromotionForm';
import { PrintableTickets, PrintFormat, usePrintTickets } from 'features/common/hooks/usePrintTickets';
import { PunchupPrintTicketButtons } from 'features/common/components/Punchup/composites/tickets/PunchupPrintTicketButtons';
import { BoxOfficeAddons, ApplicableAddon, AddonSelection, AddonSelections, toAddonsPayload } from './BoxOfficeAddons';

import styles from './PunchupBoxOffice.module.css';

type Quantities = Record<number, number>;
type PaymentMethod = 'card' | 'cash';

interface TicketTypeDicItem {
	name: string;
	quantity: number;
	ticket_price: number;
	net_price_per_ticket: number;
	discount_applied: number;
	number_of_applied_promocodes: number;
	promocode_id: string;
	seats_ids?: string[];
}

type BoxOfficeFeeDecision = 'default' | 'custom' | 'none';
type BoxOfficeFeeType = 'flat' | 'percentage';

// The operator's decision on the venue's box office fee for THIS sale: charge the venue default,
// charge another amount, or waive it. Honoured by the API only on a box-office channel.
interface BoxOfficeFeeOverride {
	decision: BoxOfficeFeeDecision;
	amount?: number;
	type?: BoxOfficeFeeType;
}

// GET /partners/box-office-fee — the venue's default; `fee` is absent when none is configured.
interface VenueBoxOfficeFee {
	configured: boolean;
	fee?: { id: number; name: string; amount: number; type: BoxOfficeFeeType; channel: string; taxable: boolean };
}

interface SendTicketsPayload {
	email: string;
	full_name?: string;
	payment_type: 'box';
	transaction_type: 'card_box_office' | 'cash_box_office';
	// The operator's per-sale decision on the venue's box office fee (only sent when the venue has one).
	box_office_fee?: BoxOfficeFeeOverride;
	ticket_types: Record<string, TicketTypeDicItem>;
	// Promo code string — the backend resolves it to a promotion and applies the discount itself.
	code?: string;
	// seats.io channel key, when the order draws on a channel's reserved inventory.
	comp_code?: string;
	// Selected add-ons (PurchaseAddonDetail[]) — addon_id + quantity + chosen variation ids.
	addons?: Array<{ addon_id: number; quantity: number; selected_variations: number[] }>;
	// Backend-calculated applied amounts from the last /calculate-totals response — the purchase
	// endpoint rejects entries with applied_amount <= 0 (binding gt=0), so zero-applied cards are dropped.
	gift_cards?: Array<{ code: string; applied_amount: number }>;
	// Buyer phone — the backend's ResolvePurchasePhone reads billing_details.phone first.
	billing_details?: { phone?: string };
	session_token_id?: string;
	last4?: string;
	// The chart session's seats.io hold token — a whole GA area (a suite) only books under the hold.
	seats_hold_token?: string;
}

interface SendTicketsResponseRaw {
	tickets?: Array<{ ticket_id?: number; ID?: number }>;
}

interface PromotionListItem {
	id: number;
	name?: string;
	amount?: number;
	discount_type?: string;
	codes?: Array<{ code?: string }>;
}

interface PromotionsResponse {
	// The endpoint serialises this with a capital P (Go field name, no json tag).
	Promotions?: PromotionListItem[];
}

interface AddonDetail {
	product_id: number;
	addon_name: string;
	product_name?: string;
	quantity: number;
	subtotal: number;
}

interface CalculateTotalsResponse {
	subtotal: number;
	fees: number;
	processor_fees?: number;
	// The venue's box office fee, summed over the cart. It is a custom fee, so it is ALSO inside
	// `fees`; it is published beside it only so the summary can label the line. Present only when
	// the quote was priced on a box-office channel and the venue has a fee.
	box_office_fees?: number;
	box_office_fee_name?: string;
	taxes: number;
	total: number;
	// Discount amount applied by the promo code. `subtotal` is already net of this.
	discount?: number;
	promo_code_applied?: boolean;
	promo_code_error?: string;
	addon_details?: AddonDetail[];
	// Per-card split of the balance actually consumed by this order; `total` is already net of it.
	gift_cards_applied?: GiftCardAppliedDetail[];
}

interface GiftCardAppliedDetail {
	code: string;
	applied_amount: number;
	balance: number;
}

interface ValidateGiftCardResponse {
	is_valid: boolean;
	balance: number;
	last4?: string;
	error?: string;
}

// A card the operator validated and attached to the order. The consumed amount lives in the totals
// response (gift_cards_applied), not here — the backend owns the split across cards.
interface AppliedGiftCard {
	code: string;
	balance: number;
	last4?: string;
}

interface ApplicableAddonsResponse {
	applicable_addons?: ApplicableAddon[];
}

interface RecentOrder {
	id: string;
	placedAt: number;
	email: string;
	fullName?: string;
	total: number;
	ticketCount: number;
	ticketIds: number[];
	dbTransactionId?: number;
	paymentMethod: PaymentMethod;
	reference?: string;
}

const formatTime = (timestamp: number) =>
	new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Short readable reference for thermal receipts: BOX-YYYYMMDD-XXXXX (uppercase base36).
const generateReferenceCode = (): string => {
	const now = new Date();
	const ymd =
		now.getFullYear().toString() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
	const random = Math.random().toString(36).slice(2, 7).toUpperCase().padEnd(5, '0');
	return `BOX-${ymd}-${random}`;
};

const formatPrice = (value: number) => `$${value.toFixed(2)}`;

// Mirrors the comped-tickets / send-tickets `generatePlaceholderEmail(firstName, lastName)` helper
// (features/PunchupPages/CompedTickets/SendCompedTicketsModal/utils.ts:159). Returns a real
// hello+...@punchup.live address so the email column is never empty on the order record. Falls
// back to a short random suffix when no full name is provided so the address still resolves.
const generatePlaceholderEmail = (fullName: string): string => {
	const cleaned = fullName.replace(/[^a-zA-Z\s]/g, '').trim();
	if (!cleaned) {
		const suffix = Math.random().toString(36).slice(2, 7).toUpperCase().padEnd(5, '0');
		return `hello+box${suffix}@punchup.live`;
	}
	const parts = cleaned.split(/\s+/);
	const firstName = parts[0] ?? '';
	const lastName = parts.slice(1).join('');
	return `hello+${firstName}${lastName}@punchup.live`;
};

const availableSeats = (ticket: TicketType) => Math.max(ticket.amount - ticket.sold, 0);

const buildTicketTypeDicItem = (ticket: TicketType, quantity: number, seatsIds?: string[]): TicketTypeDicItem => ({
	name: ticket.name,
	quantity,
	ticket_price: ticket.priceWithNoDiscount,
	net_price_per_ticket: ticket.initialPrice,
	discount_applied: Number((ticket.priceWithNoDiscount - ticket.initialPrice).toFixed(2)),
	number_of_applied_promocodes: 0,
	promocode_id: '',
	...(seatsIds && seatsIds.length > 0 ? { seats_ids: seatsIds } : {}),
});

export const PunchupBoxOffice: FC = () => {
	const { t } = useTranslation();
	const { event, isError } = useGetEvent();
	// Billing name for the Stripe ConfirmationToken. The card Element opts out of collecting the
	// name (fields.billingDetails.name = 'never'), so we MUST supply a non-empty value. Prefer the
	// customer's typed name, then the logged-in operator, then a generic "Box Office" — so a blank
	// name can never produce the empty-name Stripe error.
	const loggedUser = useAppSelector((state: RootState) => state.auth.loggedUser);
	const [quantities, setQuantities] = useState<Quantities>({});
	const [email, setEmail] = useState('');
	const [fullName, setFullName] = useState('');
	const [phone, setPhone] = useState('');
	// Left-column step: ticket/seat selection ('main') or the add-ons picker ('addons').
	const [leftStep, setLeftStep] = useState<'main' | 'addons'>('main');
	// addonId → { quantity, variations: customizationId → variationId }.
	const [addonSelections, setAddonSelections] = useState<AddonSelections>({});
	const [useEmailPlaceholder, setUseEmailPlaceholder] = useState(false);
	const [promoCode, setPromoCode] = useState('');
	// The applied code drives /calculate-totals and the send payload; the input above is just a draft.
	const [appliedPromoCode, setAppliedPromoCode] = useState('');
	// Server feedback for the last Apply attempt — the backend's reason when a code is rejected.
	const [promoError, setPromoError] = useState('');
	// Whether the inline "create a new promo on the fly" form is expanded.
	const [showCreatePromo, setShowCreatePromo] = useState(false);
	// Gift cards: the input is a draft; a card only joins `giftCards` after the backend validates it.
	const [giftCardCode, setGiftCardCode] = useState('');
	const [giftCards, setGiftCards] = useState<AppliedGiftCard[]>([]);
	const [giftCardError, setGiftCardError] = useState('');
	// seats.io channel key whose reserved seats the operator is selling into. '' = general sale only.
	const [selectedChannel, setSelectedChannel] = useState('');

	const emailPlaceholder = useMemo(() => generatePlaceholderEmail(fullName), [fullName]);
	const effectiveEmail = useEmailPlaceholder ? emailPlaceholder : email;
	const [reference, setReference] = useState('');
	// Default to Cash when Stripe isn't configured for this environment so the page is usable
	// without a publishable key. REACT_APP_STRIPE_PUBLIC_KEY is static, so this never flickers.
	const defaultPaymentMethod: PaymentMethod = process.env.REACT_APP_STRIPE_PUBLIC_KEY ? 'card' : 'cash';
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(defaultPaymentMethod);
	// Box office fee decision for this sale — resets to the venue default after every order.
	const [boxOfficeFeeDecision, setBoxOfficeFeeDecision] = useState<BoxOfficeFeeDecision>('default');
	const [boxOfficeFeeAmount, setBoxOfficeFeeAmount] = useState('');
	const [boxOfficeFeeType, setBoxOfficeFeeType] = useState<BoxOfficeFeeType>('flat');

	const venueBoxOfficeFeeQuery = useQuery<VenueBoxOfficeFee>({
		queryKey: ['venue-box-office-fee'],
		queryFn: () => getData('/partners/box-office-fee'),
		staleTime: 5 * 60_000,
	});
	const venueBoxOfficeFee = venueBoxOfficeFeeQuery.data?.configured ? venueBoxOfficeFeeQuery.data.fee : undefined;

	// What the quote and the purchase send: nothing when the venue has no fee (the API charges none),
	// the decision otherwise. A custom amount that is not yet a positive number falls back to the default
	// so the operator never quotes a $0 "custom" fee by accident.
	const boxOfficeFeeForTotals = useMemo<BoxOfficeFeeOverride | undefined>(() => {
		if (!venueBoxOfficeFee) return undefined;
		if (boxOfficeFeeDecision === 'custom') {
			const amount = parseFloat(boxOfficeFeeAmount);
			if (!Number.isFinite(amount) || amount <= 0) return { decision: 'default' };
			return { decision: 'custom', amount, type: boxOfficeFeeType };
		}
		return { decision: boxOfficeFeeDecision };
	}, [venueBoxOfficeFee, boxOfficeFeeDecision, boxOfficeFeeAmount, boxOfficeFeeType]);
	// Whether the Stripe Payment Element's card inputs are complete — gates the submit button on the
	// card flow. Resets to false whenever the Element remounts (payment-method switch / reset).
	const [isCardComplete, setIsCardComplete] = useState(false);
	const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
	// Bumped on every reset so the Stripe <Elements> remounts fresh for the next order. Kept STABLE
	// during a single order — including failed retries — so a failed submission never wipes the card.
	const [paymentSessionKey, setPaymentSessionKey] = useState(0);

	const isSeated = Boolean(event?.seatsChartKey);
	const seats = useSeatsSelection(event?.seatsEventKey, 'select');
	const { partnerData } = useGetPartnerData();

	// Channels are a seats.io concept, so only seated events have them. The query is deduped with
	// the rest of the dashboard via React Query, and stays disabled for unseated events.
	const { data: seatsIOEvent } = useGetSeatsIOEvent(isSeated ? event?.seatsEventKey : undefined);
	const channels = seatsIOEvent?.channels ?? [];

	const channelOptions = useMemo<PunchupFilterOption[]>(
		() => [
			{ value: '', label: t('boxOffice-channelGeneralSale', 'General sale') },
			...channels.map((channel) => ({ value: channel.key, label: channel.name })),
		],
		[channels, t]
	);

	// Active promotions for this partner — drives the searchable code picker. The backend still
	// validates applicability to the selected tickets when the operator hits Apply.
	const promotionsQuery = useQuery<PromotionsResponse>({
		queryKey: ['box-office-promotions'],
		queryFn: () => getData('/promotions?status=active&per_page=100&order_by=start_date&sort=desc'),
		staleTime: 60_000,
	});

	// One picker entry per code, grouped under its promotion name, de-duped by code string.
	const promoCodeOptions = useMemo<ComboboxOption[]>(() => {
		const options: ComboboxOption[] = [];
		const seen = new Set<string>();

		(promotionsQuery.data?.Promotions ?? []).forEach((promo) => {
			const off = promo.discount_type === 'percent' ? `${promo.amount ?? 0}% off` : `$${promo.amount ?? 0} off`;
			const group = promo.name?.trim() || t('boxOffice-promoUnnamed', 'Promotion');
			(promo.codes ?? []).forEach((entry) => {
				if (!entry.code || seen.has(entry.code)) return;
				seen.add(entry.code);
				options.push({ value: entry.code, label: entry.code, description: off, group });
			});
		});

		return options;
	}, [promotionsQuery.data, t]);
	const cardFormRef = useRef<CardPaymentFormHandle>(null);

	// Stripe.js is loaded lazily, keyed on the event's connected account. We source it from the
	// event's webpage settings (the same field the public checkout uses) — NOT partner-level
	// stripe_account: a partner can have a connected account yet still run an event on destination
	// charges. The backend only confirms on the connected account when the partner is in its
	// direct-charges list, which tracks this event field — so minting where the event says keeps
	// the ConfirmationToken on the account the backend will confirm against.
	const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
	const stripeAccount = event?.eventWebpageSettings?.stripeAccount;
	const stripePromise = useMemo(() => {
		if (!stripePublicKey) return null;
		return loadStripe(stripePublicKey, stripeAccount ? { stripeAccount } : undefined);
	}, [stripePublicKey, stripeAccount]);

	const ticketTypes = event?.ticketTypes ?? [];

	const setQuantity = (ticketId: number, next: number) => {
		setQuantities((prev) => ({ ...prev, [ticketId]: next }));
	};

	const seatedGroups = useMemo(() => {
		if (!isSeated) return [];
		return seats.selectionByCategory
			.map((group) => {
				const ticket = ticketTypes.find((tt) => tt.seatsCategoryKey === group.categoryKey);
				if (!ticket) return null;
				return { ticket, labels: group.objectLabels };
			})
			.filter((g): g is { ticket: TicketType; labels: string[] } => g !== null);
	}, [isSeated, seats.selectionByCategory, ticketTypes]);

	const seatedSubtotal = useMemo(
		() => seatedGroups.reduce((acc, g) => acc + g.labels.length * g.ticket.initialPrice, 0),
		[seatedGroups]
	);

	const unseatedSubtotal = useMemo(() => {
		if (isSeated) return 0;
		return ticketTypes.reduce((acc, ticket) => {
			const qty = quantities[ticket.ID] ?? 0;
			return acc + qty * ticket.initialPrice;
		}, 0);
	}, [isSeated, ticketTypes, quantities]);

	const localSubtotal = isSeated ? seatedSubtotal : unseatedSubtotal;

	const totalTickets = isSeated ? seats.selection.length : Object.values(quantities).reduce((acc, qty) => acc + qty, 0);

	// Ticket-type → quantity map for /calculate-totals. For seated, derive from selectionByCategory.
	const ticketsForTotals = useMemo<Record<number, number>>(() => {
		const map: Record<number, number> = {};
		if (isSeated) {
			seatedGroups.forEach((g) => {
				if (g.labels.length > 0) map[g.ticket.ID] = g.labels.length;
			});
		} else {
			ticketTypes.forEach((tt) => {
				const qty = quantities[tt.ID] ?? 0;
				if (qty > 0) map[tt.ID] = qty;
			});
		}
		return map;
	}, [isSeated, seatedGroups, ticketTypes, quantities]);

	const hasSelections = Object.keys(ticketsForTotals).length > 0;

	// Add-ons applicable to the current cart (event + selected tickets), with per-addon max quantities.
	const applicableAddonsQuery = useQuery<ApplicableAddonsResponse>({
		queryKey: ['box-office-addons', event?.id, ticketsForTotals],
		queryFn: () =>
			post('/public/ticketing/addons/applicable', {
				event_id: Number(event?.id),
				ticket_types: ticketsForTotals,
			}),
		enabled: hasSelections && Boolean(event?.id),
		staleTime: 30_000,
	});
	// Only surface add-ons that can actually be added to this cart. The endpoint can return
	// applicable-but-unavailable add-ons (max_quantity === 0, e.g. sold out for the selection); those
	// would render with a permanently-disabled stepper, so hide them — and drop the whole add-ons
	// step when nothing is left.
	const applicableAddons = (applicableAddonsQuery.data?.applicable_addons ?? []).filter(
		(applicable) => applicable.max_quantity > 0
	);
	const hasAddons = applicableAddons.length > 0;
	const addonsForTotals = useMemo(() => toAddonsPayload(addonSelections), [addonSelections]);

	// Codes-only payload for /calculate-totals — the backend computes each card's applied amount.
	const giftCardsForTotals = useMemo(() => giftCards.map((card) => ({ code: card.code })), [giftCards]);

	const totalsQueryKey = useMemo(
		() =>
			[
				'box-office-totals',
				ticketsForTotals,
				paymentMethod,
				appliedPromoCode,
				addonsForTotals,
				giftCardsForTotals,
				boxOfficeFeeForTotals,
			] as const,
		[ticketsForTotals, paymentMethod, appliedPromoCode, addonsForTotals, giftCardsForTotals, boxOfficeFeeForTotals]
	);

	const totalsQuery = useQuery<CalculateTotalsResponse>({
		queryKey: totalsQueryKey,
		queryFn: () =>
			// Only Cash skips Stripe processor fees. Card (on-page Elements) and Terminal (iOS
			// Tap-to-Pay) both run through Stripe and incur the same processing cut. The backend
			// validates `code` and returns promo_code_applied / promo_code_error / discount.
			post('/public/ticketing/calculate-totals', {
				tickets: ticketsForTotals,
				include_processor_fees: paymentMethod !== 'cash',
				sales_channel: paymentMethod === 'cash' ? 'cash_box_office' : 'card_box_office',
				box_office_fee: boxOfficeFeeForTotals,
				code: appliedPromoCode || undefined,
				addons_with_variations: addonsForTotals.length > 0 ? addonsForTotals : undefined,
				gift_cards: giftCardsForTotals.length > 0 ? giftCardsForTotals : undefined,
			}),
		enabled: hasSelections,
		keepPreviousData: true,
		staleTime: 10_000,
	});

	// When nothing's selected, ignore the query cache — keepPreviousData would otherwise leak the
	// last response back into the summary after the operator cleared seats / counters.
	const totals = hasSelections ? totalsQuery.data : undefined;
	const isTotalsLoading = hasSelections && totalsQuery.isFetching;
	const subtotal = hasSelections ? totals?.subtotal ?? localSubtotal : 0;
	const fees = hasSelections ? totals?.fees ?? 0 : 0;
	// The box office fee is inside `fees`; split it out so the summary shows two labelled lines.
	const boxOfficeFees = hasSelections ? totals?.box_office_fees ?? 0 : 0;
	const otherFees = Math.max(fees - boxOfficeFees, 0);
	const taxes = hasSelections ? totals?.taxes ?? 0 : 0;
	const total = hasSelections ? totals?.total ?? localSubtotal : 0;

	// `subtotal` from the API is already net of the promo discount, so add it back to show a gross
	// line + an explicit discount line that reconciles to the total.
	const discount = hasSelections ? totals?.discount ?? 0 : 0;
	const grossSubtotal = subtotal + discount;
	// We only ever set appliedPromoCode after the backend confirmed the code, so its presence is the
	// applied state — no need to re-derive from the totals response (which would flicker on refetch).
	const promoApplied = Boolean(appliedPromoCode);

	const giftCardsApplied = hasSelections ? totals?.gift_cards_applied ?? [] : [];
	const appliedAmountByCode: Record<string, number> = {};
	giftCardsApplied.forEach((card) => {
		appliedAmountByCode[card.code] = card.applied_amount;
	});

	// Deferred-intent Elements: `mode: 'payment'` + `paymentMethodCreation: 'manual'` lets CardPaymentForm
	// mint a ConfirmationToken from the entered card (see CardPaymentForm). Do NOT set
	// `payment_method_types` — the backend confirms with automatic payment methods, and Stripe rejects a
	// ConfirmationToken minted with an explicit method list against an automatic-methods PaymentIntent
	// ("collected ... using payment_method_types and cannot be confirmed ..."). This matches the widget.
	// `amount` must be a positive integer, which holds because Elements only mounts when totalCents > 0.
	const totalCents = Math.max(0, Math.round(total * 100));
	const stripeOptions = useMemo<StripeElementsOptions>(
		() => ({
			mode: 'payment',
			amount: totalCents,
			currency: 'usd',
			paymentMethodCreation: 'manual',
		}),
		[totalCents]
	);
	const showStripeElements = paymentMethod === 'card' && totalCents > 0 && stripePromise !== null;

	const seatsPricing = useMemo(() => {
		if (!isSeated) return undefined;
		return ticketTypes
			.filter((tt) => tt.seatsCategoryKey)
			.map((tt) => ({ category: tt.seatsCategoryKey as string, price: tt.initialPrice }));
	}, [isSeated, ticketTypes]);

	const ticketNameByCategoryKey = useMemo(() => {
		const map: Record<string, string> = {};
		ticketTypes.forEach((tt) => {
			if (tt.seatsCategoryKey) map[tt.seatsCategoryKey] = tt.name;
		});
		return map;
	}, [ticketTypes]);

	const updateAddon = (addonId: number, selection: AddonSelection) => {
		setAddonSelections((prev) => ({ ...prev, [addonId]: selection }));
	};

	const buildPayload = (card?: { stripeTokenId: string; last4: string }): SendTicketsPayload | null => {
		const ticket_types: Record<string, TicketTypeDicItem> = {};

		if (isSeated) {
			seatedGroups.forEach((group) => {
				ticket_types[group.ticket.ID] = buildTicketTypeDicItem(group.ticket, group.labels.length, group.labels);
			});
		} else {
			ticketTypes.forEach((ticket) => {
				const qty = quantities[ticket.ID] ?? 0;
				if (qty > 0) ticket_types[ticket.ID] = buildTicketTypeDicItem(ticket, qty);
			});
		}

		if (Object.keys(ticket_types).length === 0) return null;

		// Send the amounts the totals endpoint computed — the backend re-validates each card and
		// splits the charge, and the purchase DTO requires applied_amount > 0 per entry.
		const giftCardsPayload = (totals?.gift_cards_applied ?? [])
			.filter((card) => card.applied_amount > 0)
			.map((card) => ({ code: card.code, applied_amount: card.applied_amount }));

		return {
			email: effectiveEmail,
			full_name: fullName || undefined,
			payment_type: 'box',
			transaction_type: paymentMethod === 'cash' ? 'cash_box_office' : 'card_box_office',
			box_office_fee: boxOfficeFeeForTotals,
			ticket_types,
			// Only forward a code the backend actually accepted, so an invalid draft never blocks the order.
			code: promoApplied ? appliedPromoCode : undefined,
			comp_code: selectedChannel || undefined,
			addons: addonsForTotals.length > 0 ? addonsForTotals : undefined,
			gift_cards: giftCardsPayload.length > 0 ? giftCardsPayload : undefined,
			billing_details: phone.trim() ? { phone: phone.trim() } : undefined,
			session_token_id: card?.stripeTokenId,
			last4: card?.last4 || undefined,
			seats_hold_token: isSeated && seats.holdToken.trim() ? seats.holdToken : undefined,
		};
	};

	// Switching payment method remounts the card Element, so its previous completeness no longer holds.
	const selectPaymentMethod = (method: PaymentMethod) => {
		setPaymentMethod(method);
		setIsCardComplete(false);
	};

	const resetForm = () => {
		setQuantities({});
		setEmail('');
		setFullName('');
		setPhone('');
		setUseEmailPlaceholder(false);
		setPromoCode('');
		setAppliedPromoCode('');
		setPromoError('');
		setShowCreatePromo(false);
		setGiftCardCode('');
		setGiftCards([]);
		setGiftCardError('');
		setSelectedChannel('');
		setAddonSelections({});
		setLeftStep('main');
		setReference('');
		setPaymentMethod(defaultPaymentMethod);
		setBoxOfficeFeeDecision('default');
		setBoxOfficeFeeAmount('');
		setBoxOfficeFeeType('flat');
		setIsCardComplete(false);
		setPaymentSessionKey((key) => key + 1);
		seats.reset();
	};

	const { mutate: sendOrder, isLoading: isSending } = useMutation({
		// `post` from services/api already unwraps response.data — don't read .data again.
		mutationFn: (payload: SendTicketsPayload): Promise<SendTicketsResponseRaw> =>
			post('/tickets/multiple_purchase_bluesnap', payload),
		onSuccess: (rawData) => {
			// camelize keeps all-uppercase keys, so the transaction's gorm ID stays `ID`.
			const data = camelize(rawData ?? {}) as { ID?: number; tickets?: Array<{ ticketId?: number; ID?: number }> };
			const ticketIds = (data?.tickets ?? [])
				.map((tk) => tk.ticketId ?? tk.ID)
				.filter((id): id is number => typeof id === 'number');

			const order: RecentOrder = {
				id: `order-${Date.now()}`,
				placedAt: Date.now(),
				email: effectiveEmail,
				fullName: fullName || undefined,
				total,
				ticketCount: totalTickets,
				ticketIds,
				dbTransactionId: typeof data?.ID === 'number' ? data.ID : undefined,
				paymentMethod,
				reference: reference || undefined,
			};
			setRecentOrders((prev) => [order, ...prev]);

			postToast(
				'success',
				t('boxOffice-orderComplete', 'Order complete'),
				useEmailPlaceholder
					? t('boxOffice-placeholderEmailUsed', 'No email on file — placeholder recorded.')
					: email
					? `${t('boxOffice-confirmationEmailed', 'Confirmation emailed to')} ${email}`
					: undefined
			);

			// Clear the form for the next sale immediately instead of parking on a confirmation panel.
			// The completed order stays printable in the Recent orders list below. resetForm also clears
			// the promo (single-use) and bumps the Stripe session key so the card Element remounts fresh.
			resetForm();
		},
		onError: (err: unknown) => {
			// Surface the backend's reason (e.g. "phone number is required") instead of a generic retry.
			const body = (err as { response?: { data?: unknown } })?.response?.data;
			const serverMessage = typeof body === 'string' ? body : undefined;
			postToast(
				'error',
				t('boxOffice-sendFailed', 'Order failed'),
				serverMessage || t('boxOffice-sendFailedDescription', 'Please try again.')
			);
		},
	});

	const [isCollectingCard, setIsCollectingCard] = useState(false);

	const handleProcessOrder = async () => {
		// Card payment with a non-zero total: collect a Stripe ConfirmationToken first, then send.
		// Free orders ($0 total) and Cash/Terminal don't need a card.
		if (paymentMethod === 'card' && total > 0) {
			if (!cardFormRef.current) {
				postToast(
					'error',
					t('boxOffice-sendFailed', 'Order failed'),
					t('boxOffice-stripeNotReady', 'Card form is not ready yet — try again in a moment.')
				);
				return;
			}
			setIsCollectingCard(true);
			try {
				const card = await cardFormRef.current.collect({
					email: effectiveEmail,
					name: fullName || loggedUser?.full_name || 'Box Office',
				});
				const payload = buildPayload(card);
				if (!payload) return;
				sendOrder(payload);
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Card details are invalid.';
				postToast('error', t('boxOffice-sendFailed', 'Order failed'), message);
			} finally {
				setIsCollectingCard(false);
			}
			return;
		}

		const payload = buildPayload();
		if (!payload) return;
		sendOrder(payload);
	};

	const { printingKey, isPrinting, print } = usePrintTickets({
		onError: () =>
			postToast(
				'error',
				t('boxOffice-printFailed', 'Print failed'),
				t('boxOffice-printFailedDescription', 'Could not fetch or open one or more ticket PDFs.')
			),
	});

	const printableOrder = (order: RecentOrder): PrintableTickets => ({
		transactionId: order.dbTransactionId,
		ticketIds: order.ticketIds,
	});

	const printingFormatFor = (order: RecentOrder): PrintFormat | null => {
		if (printingKey === `${order.id}:thermal`) return 'thermal';
		if (printingKey === `${order.id}:standard`) return 'standard';
		return null;
	};

	// Apply runs an explicit validation call so every click hits the backend (a plain state-set would
	// no-op when the operator re-applies the same code). On success we lock in appliedPromoCode, which
	// the summary totals query and the order payload both read.
	const { mutate: validatePromo, isLoading: isValidatingPromo } = useMutation({
		mutationFn: (code: string): Promise<CalculateTotalsResponse> =>
			post('/public/ticketing/calculate-totals', {
				tickets: ticketsForTotals,
				include_processor_fees: paymentMethod !== 'cash',
				sales_channel: paymentMethod === 'cash' ? 'cash_box_office' : 'card_box_office',
				box_office_fee: boxOfficeFeeForTotals,
				code,
			}),
		onSuccess: (data, code) => {
			if (data.promo_code_applied) {
				setAppliedPromoCode(code);
				setPromoError('');
			} else {
				setAppliedPromoCode('');
				setPromoError(
					data.promo_code_error || t('boxOffice-promoNotApplicable', 'This code is not valid for the selected tickets.')
				);
			}
		},
		onError: () => {
			setAppliedPromoCode('');
			setPromoError(t('boxOffice-promoCheckFailed', 'Could not check this code. Please try again.'));
		},
	});

	const handleApplyPromo = () => {
		const trimmed = promoCode.trim();
		if (!trimmed) return;
		if (!hasSelections) {
			// The backend resolves the promo against the selected tickets, so there's nothing to validate yet.
			setPromoError(t('boxOffice-promoNeedsTickets', 'Add tickets first — the code applies to your selection.'));
			return;
		}
		setPromoError('');
		validatePromo(trimmed);
	};

	const handleRemovePromo = () => {
		setPromoCode('');
		setAppliedPromoCode('');
		setPromoError('');
	};

	// Unlike promo codes, validation is independent of the cart — the card is checked against the
	// Punchup gift-card service (via partner_id → punchup id), so it can be attached before tickets.
	// An invalid code answers 400, so the rejection lands in onError.
	const { mutate: validateGiftCard, isLoading: isValidatingGiftCard } = useMutation({
		mutationFn: (code: string): Promise<ValidateGiftCardResponse> =>
			post('/public/ticketing/giftcards/validate', { code, partner_id: String(event?.partnerId ?? '') }),
		onSuccess: (data, code) => {
			if (data.is_valid && data.balance > 0) {
				setGiftCards((prev) => [...prev, { code, balance: data.balance, last4: data.last4 }]);
				setGiftCardCode('');
				setGiftCardError('');
			} else if (data.is_valid) {
				setGiftCardError(t('boxOffice-giftCardNoBalance', 'This gift card has no remaining balance.'));
			} else {
				setGiftCardError(t('boxOffice-giftCardInvalid', 'This gift card code is not valid.'));
			}
		},
		onError: () => {
			setGiftCardError(t('boxOffice-giftCardInvalid', 'This gift card code is not valid.'));
		},
	});

	const handleApplyGiftCard = () => {
		const trimmed = giftCardCode.trim();
		if (!trimmed) return;
		if (giftCards.some((card) => card.code === trimmed)) {
			setGiftCardError(t('boxOffice-giftCardAlreadyApplied', 'This gift card is already applied.'));
			return;
		}
		setGiftCardError('');
		validateGiftCard(trimmed);
	};

	const handleRemoveGiftCard = (code: string) => {
		setGiftCards((prev) => prev.filter((card) => card.code !== code));
		setGiftCardError('');
	};

	// Show only the tail of the code — full codes are redeemable secrets and stay off the screen.
	const maskGiftCard = (card: AppliedGiftCard) => `····${card.last4 ?? card.code.slice(-4)}`;

	// A freshly created code is meant for the order in progress, so select it, refresh the picker, and
	// apply it straight away (validation still runs against the current selection).
	const handlePromotionCreated = (code: string) => {
		setShowCreatePromo(false);
		setPromoError('');
		setPromoCode(code);
		promotionsQuery.refetch();
		if (hasSelections) validatePromo(code);
	};

	// The page participates in the iframe sub-page lifecycle via PunchupPageShell (posts sub-page-enter /
	// scroll-to-top on mount, drives the host "Box Office" breadcrumb + back-to-dashboard). Every render
	// state — error, loading, and the point-of-sale itself — goes through the same shell so it mounts on
	// entry regardless of load state.
	const withShell = (content: ReactNode) => (
		<PunchupPageShell pageName="Box Office">
			<PunchupPageContent>
				<PunchupPageHeader
					title={t('boxOffice-title', 'Box Office')}
					description={t(
						'boxOffice-description',
						'Sell tickets at the door — card or cash — and reprint receipts from this session.'
					)}
				/>
				{content}
			</PunchupPageContent>
		</PunchupPageShell>
	);

	if (isError) {
		return withShell(
			<p className={styles.emptyState}>{t('genericError', 'Something went wrong loading this event.')}</p>
		);
	}

	if (!event) {
		return withShell(
			<div className={styles.spinnerWrap}>
				<PunchupSpinner />
			</div>
		);
	}

	const cardUnavailable = paymentMethod === 'card' && total > 0 && !stripePromise;
	// When the card Element is on screen (card flow, non-zero total), the card inputs must be complete.
	const cardIncomplete = showStripeElements && !isCardComplete;
	// Buyer-phone policy from the partner: hide the field only when explicitly 'off'; require it on 'require'.
	const showPhone = partnerData?.phone_collection !== 'off';
	const phoneRequired = partnerData?.phone_collection === 'require';
	const phoneMissing = phoneRequired && !phone.trim();
	const isProcessDisabled =
		totalTickets === 0 ||
		!effectiveEmail ||
		isSending ||
		isCollectingCard ||
		cardUnavailable ||
		cardIncomplete ||
		phoneMissing;
	// First unmet requirement, shown under the button so the operator isn't left guessing why it's
	// greyed out. Order mirrors isProcessDisabled; the busy states show a spinner instead of a hint.
	const processHint =
		totalTickets === 0
			? t('boxOffice-hintAddTicket', 'Add at least one ticket to continue')
			: !effectiveEmail
			? t('boxOffice-hintEnterEmail', "Enter the buyer's email to continue")
			: phoneMissing
			? t('boxOffice-hintEnterPhone', "Enter the buyer's phone number to continue")
			: cardUnavailable
			? t('boxOffice-hintCardUnavailable', 'Card payments are not configured — switch to Cash')
			: cardIncomplete
			? t('boxOffice-hintCompleteCard', 'Enter the card details to continue')
			: '';
	const inputsDisabled = isSending || isCollectingCard;

	return withShell(
		<div className={styles.page}>
			<div className={styles.body}>
				<div className={styles.leftColumn}>
					<div className={styles.fieldRow}>
						<div className={styles.field}>
							<label htmlFor="punchup-box-office-full-name" className={styles.label}>
								{t('fullName', 'Full name')}
								<span className={styles.optionalTag}>{t('optional', '(optional)')}</span>
							</label>
							<input
								id="punchup-box-office-full-name"
								type="text"
								className={styles.input}
								placeholder={t('fullName-placeholder', 'Jane Doe')}
								value={fullName}
								onChange={(e) => setFullName(e.target.value)}
								disabled={inputsDisabled}
							/>
						</div>
						<div className={styles.field}>
							<div className={styles.labelRow}>
								<label htmlFor="punchup-box-office-email" className={styles.label}>
									{t('email', 'Email')}
								</label>
								<button
									type="button"
									role="switch"
									aria-checked={!useEmailPlaceholder}
									className={`${styles.placeholderToggle}${
										!useEmailPlaceholder ? ` ${styles.placeholderToggleOn}` : ''
									}`}
									onClick={() => setUseEmailPlaceholder((prev) => !prev)}
									disabled={inputsDisabled}
								>
									<span className={styles.placeholderToggleLabel}>
										{t('boxOffice-emailReceiptToggle', 'Email receipt')}
									</span>
									<span className={styles.placeholderToggleTrack} aria-hidden>
										<span className={styles.placeholderToggleThumb} />
									</span>
								</button>
							</div>
							{/* Receipt off → email is disabled (greyed) and a placeholder address is recorded so the
							    order still has a buyer email. The placeholder is shown read-only for transparency. */}
							<input
								id="punchup-box-office-email"
								type="email"
								className={styles.input}
								placeholder={t('email-placeholder', 'buyer@example.com')}
								value={useEmailPlaceholder ? emailPlaceholder : email}
								onChange={(e) => setEmail(e.target.value)}
								disabled={inputsDisabled || useEmailPlaceholder}
								title={useEmailPlaceholder ? emailPlaceholder : undefined}
							/>
						</div>
						{showPhone && (
							<div className={styles.field}>
								<label htmlFor="punchup-box-office-phone" className={styles.label}>
									{t('phone', 'Phone')}
									{!phoneRequired && <span className={styles.optionalTag}>{t('optional', '(optional)')}</span>}
								</label>
								<input
									id="punchup-box-office-phone"
									type="tel"
									className={styles.input}
									placeholder={t('phone-placeholder', '+1 555 123 4567')}
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									disabled={inputsDisabled}
									autoComplete="tel"
								/>
							</div>
						)}
					</div>

					<div>
						<div className={styles.seatsHeader}>
							{leftStep === 'addons' ? (
								<>
									<h2 className={styles.sectionTitle}>{t('addons', 'Add-ons')}</h2>
									<button type="button" className={styles.stepNav} onClick={() => setLeftStep('main')}>
										<ChevronLeft size={16} aria-hidden />
										{t('back', 'Back')}
									</button>
								</>
							) : (
								<>
									<h2 className={styles.sectionTitle}>{isSeated ? t('seats', 'Seats') : t('tickets', 'Tickets')}</h2>
									<div className={styles.seatsHeaderRight}>
										{isSeated && channels.length > 0 && (
											<div className={styles.channelPicker}>
												<span className={styles.channelPickerLabel}>{t('boxOffice-channelLabel', 'Channel')}</span>
												<PunchupFilterSelect
													ariaLabel={t('boxOffice-channelSelectAria', 'Select a sales channel')}
													options={channelOptions}
													value={selectedChannel}
													onChange={setSelectedChannel}
													menuAlign="right"
												/>
											</div>
										)}
										{hasAddons && (
											<button type="button" className={styles.stepNav} onClick={() => setLeftStep('addons')}>
												{t('boxOffice-addonsStep', 'Add-ons')}
												<ChevronRight size={16} aria-hidden />
											</button>
										)}
									</div>
								</>
							)}
						</div>

						{leftStep === 'addons' ? (
							<BoxOfficeAddons
								applicableAddons={applicableAddons}
								selections={addonSelections}
								onChange={updateAddon}
								formatPrice={formatPrice}
								disabled={inputsDisabled}
							/>
						) : isSeated ? (
							<div className={styles.seatsWrap}>
								<PunchupBuyerSeatsWidget
									eventKey={event.seatsEventKey}
									chartProps={seats.chartProps}
									pricing={seatsPricing}
									priceFormatter={formatPrice}
									objectTooltip={{ showCategory: false }}
									tooltipInfo={(object) => {
										const key = object.category?.key;
										return key ? ticketNameByCategoryKey[String(key)] ?? '' : '';
									}}
									session="start"
									// NO_CHANNEL keeps general seats selectable alongside the chosen channel's reserved block,
									// so the operator can sell both. seats.io rejects an empty array — pass undefined for general-only.
									channels={selectedChannel ? ['NO_CHANNEL', selectedChannel] : undefined}
								/>
							</div>
						) : (
							<div className={styles.ticketList}>
								{ticketTypes.length === 0 ? (
									<p className={styles.emptyState}>{t('boxOffice-noTicketsAvailable', 'No tickets available.')}</p>
								) : (
									ticketTypes.map((ticket) => {
										const qty = quantities[ticket.ID] ?? 0;
										const max = availableSeats(ticket);
										const disabled = ticket.soldOut || max === 0;
										return (
											<div
												key={ticket.ID}
												className={`${styles.ticketCard}${qty > 0 ? ` ${styles.ticketCardActive}` : ''}`}
											>
												<div className={styles.ticketInfo}>
													<h2 className={styles.ticketName}>{ticket.name}</h2>
													{ticket.description && <p className={styles.ticketDescription}>{ticket.description}</p>}
												</div>
												<span className={styles.ticketPrice}>{formatPrice(ticket.initialPrice)}</span>
												{disabled ? (
													<span className={styles.soldOutBadge}>{t('soldOut', 'Sold out')}</span>
												) : (
													<div className={styles.stepper}>
														<button
															type="button"
															className={styles.stepperButton}
															onClick={() => setQuantity(ticket.ID, Math.max(qty - 1, 0))}
															disabled={qty === 0 || inputsDisabled}
															aria-label={t('decrement', 'Decrement')}
														>
															<Minus size={16} aria-hidden />
														</button>
														<span className={styles.stepperCount}>{qty}</span>
														<button
															type="button"
															className={styles.stepperButton}
															onClick={() => setQuantity(ticket.ID, Math.min(qty + 1, max))}
															disabled={qty >= max || inputsDisabled}
															aria-label={t('increment', 'Increment')}
														>
															<Plus size={16} aria-hidden />
														</button>
													</div>
												)}
											</div>
										);
									})
								)}
							</div>
						)}
					</div>
				</div>

				<aside className={styles.summary}>
					<h2 className={styles.summaryTitle}>{t('orderSummary', 'Order summary')}</h2>
					{isSeated ? (
						seatedGroups.length === 0 ? (
							<p className={styles.emptyState}>{t('selectSeatsToContinue', 'Select seats to continue.')}</p>
						) : (
							seatedGroups.map((group) => (
								<div key={group.ticket.ID} className={styles.summaryGroup}>
									<div className={styles.summaryTicketRow}>
										<span>{`${group.labels.length} x ${group.ticket.name}`}</span>
										<span>{formatPrice(group.labels.length * group.ticket.initialPrice)}</span>
									</div>
									<div className={styles.summarySeatList}>
										{Array.from(new Set(group.labels)).map((label) => (
											<span key={label} className={styles.summarySeatLabel}>
												{label}
											</span>
										))}
									</div>
								</div>
							))
						)
					) : (
						<div className={styles.summaryRow}>
							<span>{t('tickets', 'Tickets')}</span>
							<span>{totalTickets}</span>
						</div>
					)}
					{(totals?.addon_details ?? []).map((addon) => (
						<div key={`${addon.product_id}-${addon.product_name ?? ''}`} className={styles.summaryRow}>
							<span>{`${addon.quantity} x ${addon.addon_name}${
								addon.product_name ? ` · ${addon.product_name}` : ''
							}`}</span>
							<span>{formatPrice(addon.subtotal)}</span>
						</div>
					))}
					<div className={styles.summaryRow}>
						<span>{t('subtotal', 'Subtotal')}</span>
						<span>{formatPrice(grossSubtotal)}</span>
					</div>
					{discount > 0 && (
						<div className={`${styles.summaryRow} ${styles.summaryDiscountRow}`}>
							<span>{t('discount', 'Discount')}</span>
							<span>{`−${formatPrice(discount)}`}</span>
						</div>
					)}
					{giftCardsApplied
						.filter((card) => card.applied_amount > 0)
						.map((card) => (
							<div key={card.code} className={`${styles.summaryRow} ${styles.summaryDiscountRow}`}>
								<span>{`${t('boxOffice-giftCardRow', 'Gift card')} ····${card.code.slice(-4)}`}</span>
								<span>{`−${formatPrice(card.applied_amount)}`}</span>
							</div>
						))}
					{otherFees > 0 && (
						<div className={styles.summaryRow}>
							<span>
								{t('fees', 'Fees')}
								{isTotalsLoading && ' …'}
							</span>
							<span>{formatPrice(otherFees)}</span>
						</div>
					)}
					{boxOfficeFees > 0 && (
						<div className={styles.summaryRow}>
							<span>
								{totals?.box_office_fee_name || t('boxOffice-feeRow', 'Box office fee')}
								{isTotalsLoading && ' …'}
							</span>
							<span>{formatPrice(boxOfficeFees)}</span>
						</div>
					)}
					{taxes > 0 && (
						<div className={styles.summaryRow}>
							<span>{t('taxes', 'Taxes')}</span>
							<span>{formatPrice(taxes)}</span>
						</div>
					)}
					<div className={styles.summaryTotalRow}>
						<span>{t('total', 'Total')}</span>
						<span>{formatPrice(total)}</span>
					</div>

					<div className={styles.field}>
						<label htmlFor="punchup-box-office-promo" className={styles.label}>
							{t('promoCode', 'Promo code')}
							<span className={styles.optionalTag}>{t('optional', '(optional)')}</span>
						</label>
						{promoApplied ? (
							<div className={styles.promoApplied} role="status" aria-live="polite">
								<Tag size={14} aria-hidden />
								<span className={styles.promoAppliedCode}>{appliedPromoCode}</span>
								<span className={styles.promoAppliedNote}>{t('boxOffice-promoAppliedTag', 'applied')}</span>
								<button
									type="button"
									className={styles.promoRemove}
									onClick={handleRemovePromo}
									disabled={inputsDisabled}
									aria-label={t('boxOffice-promoRemove', 'Remove promo code')}
								>
									<X size={14} aria-hidden />
								</button>
							</div>
						) : (
							<>
								<div className={styles.promoInputRow}>
									<div className={styles.promoCombobox}>
										<PunchupCombobox
											options={promoCodeOptions}
											value={promoCode || null}
											onChange={(value) => {
												setPromoError('');
												setPromoCode(String(value));
											}}
											placeholder={t('boxOffice-promoSelect', 'Select a code')}
											searchPlaceholder={t('boxOffice-promoSearch', 'Search codes…')}
											isLoading={promotionsQuery.isLoading}
											emptyLabel={t('boxOffice-promoNoCodes', 'No codes yet — create one with +')}
										/>
									</div>
									<PunchupButton
										variant="outline"
										type="button"
										onClick={handleApplyPromo}
										isLoading={isValidatingPromo}
										disabled={inputsDisabled || !promoCode.trim()}
									>
										{t('apply', 'Apply')}
									</PunchupButton>
									<button
										type="button"
										className={styles.promoCreateToggle}
										onClick={() => setShowCreatePromo((open) => !open)}
										disabled={inputsDisabled}
										aria-expanded={showCreatePromo}
										aria-label={t('boxOffice-promoCreateNew', 'Create a new promo code')}
										title={t('boxOffice-promoCreateNew', 'Create a new promo code')}
									>
										<Plus size={16} aria-hidden />
									</button>
								</div>
								{showCreatePromo && (
									<NewPromotionForm
										eventId={event.id}
										disabled={inputsDisabled}
										onCancel={() => setShowCreatePromo(false)}
										onCreated={handlePromotionCreated}
									/>
								)}
							</>
						)}
						{promoError && <p className={styles.promoError}>{promoError}</p>}
					</div>

					<div className={styles.field}>
						<label htmlFor="punchup-box-office-gift-card" className={styles.label}>
							{t('boxOffice-giftCardLabel', 'Gift card')}
							<span className={styles.optionalTag}>{t('optional', '(optional)')}</span>
						</label>
						<div className={styles.promoInputRow}>
							<input
								id="punchup-box-office-gift-card"
								type="text"
								className={styles.input}
								placeholder={t('boxOffice-giftCardPlaceholder', 'Enter gift card code')}
								value={giftCardCode}
								onChange={(e) => {
									setGiftCardError('');
									setGiftCardCode(e.target.value);
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleApplyGiftCard();
								}}
								disabled={inputsDisabled || isValidatingGiftCard}
								autoComplete="off"
							/>
							<PunchupButton
								variant="outline"
								type="button"
								onClick={handleApplyGiftCard}
								isLoading={isValidatingGiftCard}
								disabled={inputsDisabled || !giftCardCode.trim()}
								aria-label={t('boxOffice-giftCardApply', 'Apply gift card')}
							>
								{t('apply', 'Apply')}
							</PunchupButton>
						</div>
						{giftCards.map((card) => {
							const appliedAmount = appliedAmountByCode[card.code];
							return (
								<div key={card.code} className={styles.promoApplied} role="status" aria-live="polite">
									<Gift size={14} aria-hidden />
									<span className={styles.promoAppliedCode}>{maskGiftCard(card)}</span>
									<span className={styles.promoAppliedNote}>
										{appliedAmount !== undefined && appliedAmount > 0
											? `−${formatPrice(appliedAmount)}`
											: `${formatPrice(card.balance)} ${t('boxOffice-giftCardAvailable', 'available')}`}
									</span>
									<button
										type="button"
										className={styles.promoRemove}
										onClick={() => handleRemoveGiftCard(card.code)}
										disabled={inputsDisabled}
										aria-label={`${t('boxOffice-giftCardRemove', 'Remove gift card')} ${maskGiftCard(card)}`}
									>
										<X size={14} aria-hidden />
									</button>
								</div>
							);
						})}
						{giftCardError && <p className={styles.promoError}>{giftCardError}</p>}
					</div>

					<div className={styles.summaryDivider} />

					{venueBoxOfficeFee && (
						<div className={styles.field}>
							<span className={styles.label}>{t('boxOffice-feeLabel', 'Box office fee')}</span>
							<div
								className={styles.toggleRow}
								role="radiogroup"
								aria-label={t('boxOffice-feeLabel', 'Box office fee')}
							>
								<button
									type="button"
									role="radio"
									aria-checked={boxOfficeFeeDecision === 'default'}
									className={boxOfficeFeeDecision === 'default' ? styles.toggleOptionActive : styles.toggleOption}
									onClick={() => setBoxOfficeFeeDecision('default')}
									disabled={inputsDisabled}
								>
									{`${venueBoxOfficeFee.name || t('boxOffice-feeDefault', 'Venue fee')} · ${
										venueBoxOfficeFee.type === 'percentage'
											? `${venueBoxOfficeFee.amount}%`
											: formatPrice(venueBoxOfficeFee.amount)
									}`}
								</button>
								<button
									type="button"
									role="radio"
									aria-checked={boxOfficeFeeDecision === 'custom'}
									className={boxOfficeFeeDecision === 'custom' ? styles.toggleOptionActive : styles.toggleOption}
									onClick={() => setBoxOfficeFeeDecision('custom')}
									disabled={inputsDisabled}
								>
									{t('boxOffice-feeOther', 'Other amount')}
								</button>
								<button
									type="button"
									role="radio"
									aria-checked={boxOfficeFeeDecision === 'none'}
									className={boxOfficeFeeDecision === 'none' ? styles.toggleOptionActive : styles.toggleOption}
									onClick={() => setBoxOfficeFeeDecision('none')}
									disabled={inputsDisabled}
								>
									{t('boxOffice-feeNone', 'No fee')}
								</button>
							</div>
							{boxOfficeFeeDecision === 'custom' && (
								<div className={styles.promoInputRow}>
									<input
										id="punchup-box-office-fee-amount"
										type="text"
										inputMode="decimal"
										className={styles.input}
										placeholder={boxOfficeFeeType === 'percentage' ? '7.5' : '2.00'}
										aria-label={t('boxOffice-feeAmount', 'Box office fee amount')}
										value={boxOfficeFeeAmount}
										onChange={(e) => {
											const next = e.target.value.replace(/,/g, '.');
											if (next === '' || /^[0-9]*\.?[0-9]*$/.test(next)) setBoxOfficeFeeAmount(next);
										}}
										disabled={inputsDisabled}
										autoComplete="off"
									/>
									<div
										className={styles.toggleRow}
										role="radiogroup"
										aria-label={t('boxOffice-feeType', 'Box office fee type')}
									>
										<button
											type="button"
											role="radio"
											aria-checked={boxOfficeFeeType === 'flat'}
											className={boxOfficeFeeType === 'flat' ? styles.toggleOptionActive : styles.toggleOption}
											onClick={() => setBoxOfficeFeeType('flat')}
											disabled={inputsDisabled}
										>
											$
										</button>
										<button
											type="button"
											role="radio"
											aria-checked={boxOfficeFeeType === 'percentage'}
											className={boxOfficeFeeType === 'percentage' ? styles.toggleOptionActive : styles.toggleOption}
											onClick={() => setBoxOfficeFeeType('percentage')}
											disabled={inputsDisabled}
										>
											%
										</button>
									</div>
								</div>
							)}
						</div>
					)}

					<div className={styles.field}>
						<span className={styles.label}>{t('boxOffice-tender', 'Tender')}</span>
						<div className={styles.toggleRow} role="radiogroup" aria-label={t('boxOffice-tender', 'Tender')}>
							<button
								type="button"
								role="radio"
								aria-checked={paymentMethod === 'card'}
								className={paymentMethod === 'card' ? styles.toggleOptionActive : styles.toggleOption}
								onClick={() => selectPaymentMethod('card')}
								disabled={inputsDisabled || !stripePromise}
								title={
									!stripePromise
										? t(
												'boxOffice-stripeNotConfigured',
												'Stripe is not configured for this environment. Add REACT_APP_STRIPE_PUBLIC_KEY to use Card payments.'
										  )
										: undefined
								}
							>
								{t('card', 'Card')}
							</button>
							<button
								type="button"
								role="radio"
								aria-checked={paymentMethod === 'cash'}
								className={paymentMethod === 'cash' ? styles.toggleOptionActive : styles.toggleOption}
								onClick={() => selectPaymentMethod('cash')}
								disabled={inputsDisabled}
							>
								{t('cash', 'Cash')}
							</button>
						</div>
					</div>

					{paymentMethod === 'card' &&
						(showStripeElements ? (
							<Elements key={paymentSessionKey} options={stripeOptions} stripe={stripePromise}>
								<CardPaymentForm ref={cardFormRef} onCompleteChange={setIsCardComplete} />
							</Elements>
						) : !stripePromise ? (
							<p className={styles.stripeFormHint}>
								{t(
									'boxOffice-stripeNotConfigured',
									'Stripe is not configured for this environment. Add REACT_APP_STRIPE_PUBLIC_KEY to use Card payments.'
								)}
							</p>
						) : null)}

					<div className={styles.field}>
						<label htmlFor="punchup-box-office-reference" className={styles.label}>
							{t('boxOffice-referenceLabel', 'Reference')}
							<span className={styles.optionalTag}>{t('optional', '(optional)')}</span>
						</label>
						<input
							id="punchup-box-office-reference"
							type="text"
							className={styles.input}
							value={reference}
							onChange={(e) => setReference(e.target.value)}
							disabled={inputsDisabled}
							autoComplete="off"
						/>
						<button
							type="button"
							className={styles.generateLink}
							onClick={() => setReference(generateReferenceCode())}
							disabled={inputsDisabled}
							title={t(
								'boxOffice-generateReferenceHint',
								'Generate a unique BOX-YYYYMMDD-XXXXX reference for this order.'
							)}
						>
							{t('boxOffice-generate', 'Generate')}
						</button>
					</div>

					<div className={styles.actions}>
						<PunchupButton
							variant="primary"
							disabled={isProcessDisabled}
							isLoading={isSending || isCollectingCard}
							onClick={handleProcessOrder}
						>
							{t('processOrder', 'Process order')}
						</PunchupButton>
						{isProcessDisabled && !isSending && !isCollectingCard && processHint && (
							<p className={styles.processHint} role="status" aria-live="polite">
								<AlertTriangle size={18} className={styles.processHintIcon} aria-hidden />
								{processHint}
							</p>
						)}
					</div>
				</aside>
			</div>

			<section className={styles.recentSection} aria-labelledby="punchup-box-office-recent-title">
				<h2 id="punchup-box-office-recent-title" className={styles.recentTitle}>
					{t('boxOffice-recentOrders', 'Recent box office orders')}
				</h2>
				{recentOrders.length === 0 ? (
					<p className={styles.recentEmpty}>
						{t(
							'boxOffice-recentOrdersEmpty',
							'Orders placed in this session will appear here so you can re-download tickets.'
						)}
					</p>
				) : (
					<div className={styles.recentList}>
						{recentOrders.map((order) => (
							<div key={order.id} className={styles.recentRow}>
								<div className={styles.recentRowPrimary}>
									<span className={styles.recentEmail}>{order.email || t('boxOffice-noEmail', 'No email')}</span>
									<span className={styles.recentMeta}>
										{`${formatTime(order.placedAt)} · ${order.ticketCount} ${
											order.ticketCount === 1 ? t('ticket', 'ticket') : t('tickets', 'tickets')
										}`}
									</span>
								</div>
								<span className={styles.recentBadge}>
									{order.paymentMethod === 'card' ? t('card', 'Card') : t('cash', 'Cash')}
								</span>
								<span className={styles.recentTotal}>{formatPrice(order.total)}</span>
								<PunchupPrintTicketButtons
									tickets={printableOrder(order)}
									printing={printingFormatFor(order)}
									busy={isPrinting}
									onPrint={(format) => print(`${order.id}:${format}`, printableOrder(order), format)}
									labels={{
										thermal: t('boxOffice-printThermal', 'Print thermal'),
										standard: t('boxOffice-printStandard', 'Print standard'),
										thermalHint: t('boxOffice-printThermalHint', '80mm receipt-printer format.'),
										standardHint: t('boxOffice-printStandardHint', 'Letter-size ticket format.'),
										thermalUnavailable: t('boxOffice-noTicketIds', 'No ticket IDs were returned for this order.'),
										standardUnavailable: t(
											'boxOffice-printStandardUnavailable',
											'Standard format is not available for this order.'
										),
									}}
								/>
							</div>
						))}
					</div>
				)}
			</section>
		</div>
	);
};
